/**
 * Centralized utilities for navigating to results pages.
 *
 * This module provides a single source of truth for results page navigation,
 * ensuring consistent behavior for:
 * - URL structure and params
 * - sessionStorage keys for browse menu data and chat origin
 * - Chat window display behavior (maximize for agent, minimize for browse)
 *
 * ## When to use each function:
 *
 * ### `navigateToAgentResults` - Use when:
 * - User sends a message that triggers the AI agent to search for products
 * - The agent might respond with just text (no product search)
 * - You want the user redirected back to their origin page if no products
 *
 * Examples: Homepage hero search, mobile search bar, editorial card clicks,
 * outfit builder, "add to search" from navbar
 *
 * ### `browseByMenu` - Use when:
 * - User clicks on browse menu navigation (mega menu, breadcrumbs, etc.)
 * - The search is browse-menu-based, not agent-based
 * - Results are guaranteed (even if empty set)
 *
 * ### Do NOT use `navigateToAgentResults` for:
 * - "More Like This" searches (use direct `mlt-${threadId}` sessionStorage)
 * - Browse menu navigation (use `browseByMenu`)
 * - Navigating to the dedicated `/chats` page
 * - Floating chat interactions that don't navigate away
 */

import type { AppRouterInstance } from "next/dist/shared/lib/app-router-context.shared-runtime";

import type { BrowseMenuNavigationPayload } from "@/lib/browse-menu";
import { CHAT_ROUTES } from "@/lib/chat-routes";

/** SessionStorage key for tracking where user came from before agent navigation */
export const CHAT_ORIGIN_KEY = "chat-origin";

const RESULTS_NEW_SESSION_PREFIX = "results-new-";
const RESULTS_NEW_CHAT_SESSION_PREFIX = "results-new-chat-";

/** URL param to force chat window to open on redirect (used when returning to origin without results) */
export const SHOW_CHAT_PARAM = "showChat";

const getResultsNewSessionKey = (threadId: string): string =>
  `${RESULTS_NEW_SESSION_PREFIX}${threadId}`;
const getResultsNewChatSessionKey = (threadId: string): string =>
  `${RESULTS_NEW_CHAT_SESSION_PREFIX}${threadId}`;

const consumeSessionFlag = (key: string): boolean => {
  if (typeof window === "undefined") return false;
  const value = sessionStorage.getItem(key);
  if (value) {
    sessionStorage.removeItem(key);
    return true;
  }
  return false;
};

/**
 * Mark that a results navigation represents a new search intent.
 * This flag is one-shot and should be consumed by the results page/layout.
 */
export function markResultsNavigationNew(threadId: string): void {
  if (typeof window === "undefined") return;
  sessionStorage.setItem(getResultsNewSessionKey(threadId), "1");
  sessionStorage.setItem(getResultsNewChatSessionKey(threadId), "1");
}

/**
 * Check whether a results navigation should auto-maximize chat without consuming the flag.
 */
export function peekResultsNavigationNewForChat(threadId: string): boolean {
  if (typeof window === "undefined") return false;
  return sessionStorage.getItem(getResultsNewChatSessionKey(threadId)) === "1";
}

/**
 * Consume the one-shot "new navigation" flag for a results thread.
 */
export function consumeResultsNavigationNew(threadId: string): boolean {
  return consumeSessionFlag(getResultsNewSessionKey(threadId));
}

/**
 * Consume the one-shot "new navigation" flag for chat display.
 */
export function consumeResultsNavigationNewForChat(threadId: string): boolean {
  return consumeSessionFlag(getResultsNewChatSessionKey(threadId));
}

/**
 * Navigate to results page via agent chat.
 *
 * Stores the current page location in sessionStorage before navigating.
 * If the agent responds without product results, the user will be automatically
 * redirected back to this origin page. For chat-history origins, the redirect
 * will target the newly created chat thread. For other origins, `?showChat=true`
 * is appended so the floating chat window is visible.
 *
 * @example
 * ```ts
 * // Store chat data first
 * sessionStorage.setItem(`chat-${chatId}`, JSON.stringify({ query, ... }));
 * setChatCategory(chatId, categoryToken);
 *
 * // Then navigate (origin is auto-captured)
 * navigateToAgentResults(router, chatId);
 * ```
 *
 * @param router - Next.js app router instance
 * @param chatId - Unique chat/thread ID for the results page
 */
export function navigateToAgentResults(router: AppRouterInstance, chatId: string): void {
  // Store current location so we can return if no results
  if (typeof window !== "undefined") {
    sessionStorage.setItem(CHAT_ORIGIN_KEY, window.location.pathname + window.location.search);
  }
  markResultsNavigationNew(chatId);
  router.push(`/results/${chatId}?agent=true`);
}

/**
 * Get and clear the stored chat origin.
 *
 * Called by useResultsPageSearch when the agent finishes without product results.
 * Always clears the stored value after reading to prevent stale redirects.
 *
 * @returns The origin path (e.g., "/" or "/product/123?tab=details") or null
 */
export function getAndClearChatOrigin(): string | null {
  if (typeof window === "undefined") return null;
  const origin = sessionStorage.getItem(CHAT_ORIGIN_KEY);
  if (origin) {
    sessionStorage.removeItem(CHAT_ORIGIN_KEY);
  }
  return origin;
}

/**
 * Build a redirect URL for agent searches that finish without product results.
 *
 * Chat-history origins should land on the newly created thread so the selected
 * conversation remains accurate. Other origins keep the existing behavior of
 * reopening floating chat via `showChat=true`.
 *
 * @param origin - The origin path to redirect to
 * @param currentThreadId - The current/newly created chat thread id
 * @returns Redirect URL for the no-results fallback flow
 */
export function buildOriginRedirectUrl(origin: string, currentThreadId: string): string {
  if (isChatHistoryOrigin(origin)) {
    return CHAT_ROUTES.thread(currentThreadId);
  }

  const separator = origin.includes("?") ? "&" : "?";
  return `${origin}${separator}${SHOW_CHAT_PARAM}=true`;
}

function isChatHistoryOrigin(origin: string): boolean {
  const pathname = origin.split("?")[0] || origin;
  return (
    pathname === "/chats" || /^\/chats\/[^/]+$/.test(pathname) || /^\/chat\/[^/]+$/.test(pathname)
  );
}

/**
 * Navigate to results page via browse menu navigation.
 *
 * This is used when the user clicks on breadcrumbs, mega menu categories,
 * or other browse menu navigation. The chat assistant will be minimized
 * to let the user focus on browsing.
 *
 * @param router - Next.js app router instance
 * @param threadId - Unique thread ID for the results page (typically from uuidv4())
 * @param payload - Browse menu navigation payload
 */
export function browseByMenu(
  router: AppRouterInstance,
  threadId: string,
  payload: BrowseMenuNavigationPayload
): void {
  sessionStorage.setItem(`browse-menu-${threadId}`, JSON.stringify(payload));
  router.push(`/results/${threadId}?browse=true`);
}
